"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getInitials = exports.toKebabCase = exports.toAlphanumericString = exports.upperCaseFirst = void 0;
exports.isNumeral = isNumeral;
exports.toEnvarCase = toEnvarCase;
exports.formatSecondsMMSS = formatSecondsMMSS;
exports.formatBytes = formatBytes;
exports.stripHTML = stripHTML;
exports.toCamelCase = toCamelCase;
exports.toSnakeCase = toSnakeCase;
exports.toPascalCase = toPascalCase;
exports.toTitleCase = toTitleCase;
exports.afterLast = afterLast;
const lodash_1 = require("lodash");
function isNumeral(s) {
    if (!s) {
        return false;
    }
    return s >= '0' && s <= '9';
}
function toEnvarCase(s) {
    let c = null;
    let p = null;
    const words = [];
    let buf = '';
    for (let i = 0; i < s.length; ++i) {
        c = s[i];
        if (c >= 'a' && c <= 'z') {
            if (p !== null) {
                if (buf.length >= 2) {
                    words.push(buf.substring(0, buf.length - 1));
                    buf = p;
                }
                p = null;
            }
            buf += c.toUpperCase();
        }
        else if (c >= 'A' && c <= 'Z') {
            if (p === null && (i === 0 || !isNumeral(s[i - 1]))) {
                if (buf.length > 0) {
                    words.push(buf);
                }
                buf = c;
            }
            else {
                buf += c;
            }
            p = c;
        }
        else if (isNumeral(c)) {
            buf += c;
        }
        else {
            if (buf.length > 0) {
                words.push(buf);
                buf = '';
                if (i + 1 === s.length) {
                    words.push('');
                }
            }
            else {
                words.push('');
            }
            if (p !== null) {
                p = null;
            }
        }
    }
    if (buf.length > 0) {
        words.push(buf);
    }
    return words.join('_');
}
const upperCaseFirst = (t) => t.replace(/(^|\s)[A-Za-zÀ-ÖØ-öø-ÿ]/g, c => c.toUpperCase());
exports.upperCaseFirst = upperCaseFirst;
const toAlphanumericString = (str) => {
    const regex = /^[a-z0-9]+$/i;
    let builderStr = '';
    for (const char of str) {
        if (regex.test(char)) {
            builderStr += char;
        }
    }
    return builderStr;
};
exports.toAlphanumericString = toAlphanumericString;
const toKebabCase = (str) => str
    .replace(/_/g, '-')
    .replace(/[A-Z]+(?![a-z])|[A-Z]/g, ($, ofs) => (ofs ? '-' : '') + $.toLowerCase())
    .replace(/--/g, '-');
exports.toKebabCase = toKebabCase;
/**
 * Great for getting the initials of a name given the full name
 * @param value
 * @returns
 */
const getInitials = (value) => value.match(/\b(\w)/g)?.join('') || '';
exports.getInitials = getInitials;
function formatSecondsMMSS(seconds) {
    return new Date(1000 * seconds).toISOString().slice(14, 19);
}
/**
 * Provides a standard string format of bytes
 * Credit: https://stackoverflow.com/a/14919494/7977259
 * @param bytes
 * @param options
 * @returns
 */
function formatBytes(bytes, options) {
    const optionsToUse = { decimals: 2, units: 'decimal', ...options };
    const { decimals, units } = optionsToUse;
    const threshold = units === 'decimal' ? 1000 : 1024;
    if (bytes < 0)
        return 'N/A';
    if (bytes < threshold)
        return bytes + ' B';
    const unitNames = units === 'decimal'
        ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    const r = 10 ** decimals;
    do {
        bytes /= threshold;
        ++u;
    } while (Math.round(bytes * r) / r >= threshold && u < unitNames.length - 1);
    return bytes.toFixed(decimals) + ' ' + unitNames[u];
}
/**
 * Strips HTML from a string and returns the string without the HTML tags
 *
 * The first function written by GPT-3 to be included in an e11 package. (only stylistic modifications were made)
 * Ironically, written to purify inputs to GPT-3 - so this was written BY and FOR GPT-3
 * @param text
 * @returns
 */
function stripHTML(inputString) {
    const regex = /(<([^>]+)>)/gi;
    return inputString.replace(regex, '');
}
function toCamelCase(inputString) {
    return (0, lodash_1.camelCase)(inputString);
}
function toSnakeCase(str) {
    return str
        .replace(/[- .]/g, '_')
        .replace(/[A-Z]+(?![a-z])|[A-Z]/g, ($, ofs) => (ofs ? '_' : '') + $.toLowerCase())
        .replace(/__/g, '_'); // TMT: this is NOT recursive, so every underscore pair would become a single
}
/**
 * Converts a string to PascalCase
 * @param {string} str string to convert
 * @returns {string} PascalCase string
 */
function toPascalCase(str) {
    const allCaps = str === str.toUpperCase() && /[A-Z]/.test(str);
    return (0, lodash_1.startCase)(allCaps ? str.toLowerCase() : str).replace(/\s/g, '');
}
/**
 * Converts a string to Title Case
 * @param {string} str string to convert
 * @returns {string} Title Case string
 */
function toTitleCase(str) {
    const pascalCase = toPascalCase(str);
    return (pascalCase
        // Add space before capital letters, but not if they're part of an acronym
        .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Handle acronyms
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // Handle camelCase
        .replace(/([a-zA-Z])(\d+)/g, '$1 $2') // Handle numbers
        .trim());
}
/**
 * Returns part of string past last occurrence of delimiter.
 * @param {string} payload string to extract from
 * @param {string} delimiter delimiter to find last occurrence of
 * @returns {string} part of string past last occurrence of delimter, or whole payload if delimiter not found
 */
function afterLast(payload, delimiter) {
    const last = payload.lastIndexOf(delimiter);
    return last >= 0 ? payload.substring(last + 1) : payload;
}
